/**
 * NewEntryPage.jsx - AGGIORNATO per Issue 7 + UI Improvements + Nuove Opzioni Form
 * 
 * Aggiornamenti per sezioni custom:
 * - Rendering checkbox per options invece di select
 * - Mostra linkedNote quando opzione selezionata
 * - Gestione formData per campi custom (array per opzioni, string per note)
 * 
 * Aggiornamenti UI:
 * - Campo "Presente": pulsanti SI/NO invece di toggle (verde/rosso con bordo)
 * - Se presente=true: mostra sezioni Ingresso e Uscita
 * - Se presente=false: mostra "Ha comunicato?" con pulsanti SI/NO, se SI mostra motivo
 * - Segnalazione Urgente: bottone singolo che si attiva (non più collapsible)
 * 
 * AGGIORNAMENTO NUOVE OPZIONI:
 * - Accompagnato da / Ritirato da: nuove opzioni con logica Trasporto e Altro
 * - Comportamenti: nuove opzioni con ✅ Positivo, descrizione visibile solo se non positivo
 * - Compiti: nuove opzioni dettagliate
 * - Attività di gruppo: nuove opzioni comportamentali
 * - Tempo libero: nuove opzioni, textarea visibile se "Propone attività" o "Altro"
 * - Sport: pulsanti Sì/No invece di textarea
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { useDraft } from '../../hooks/useDraft'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { useVibrate } from '../../hooks/useVibrate'
import { usePolling } from '../../hooks/usePolling'
import { entriesApi, minoriApi, settingsApi, usersApi } from '../../services/api'
import { PageLoader, Card, MultiSelect, Toggle } from '../../components/ui'
import { DuplicateWarning } from '../../components/ui/DuplicateWarning'
import { SaveAnimation } from '../../components/ui/SaveAnimation'
import { DeleteEntryModal } from '../../components/ui'
import { GiornoChiusoBlocco, GiornoChiusuraParzialeWarning, AperturaStroardinariaInfo } from '../../components/ui/GiornoChiusoBlocco'
import { getStatoGiorno } from '../../hooks/useCalendarioChiusure'
import { ArrowLeft, Save, Loader2, AlertTriangle, ChevronDown, ChevronUp, Check, Edit3, Eye, MessageSquare, Trash2, X, Clock, Calendar } from 'lucide-react'

// Opzioni di fallback per multi-select (usate se il tenant non ha personalizzazioni)
// AGGIORNATO: Nuove opzioni
const FALLBACK_OPTIONS = {
  accompagnatoIngresso: [
    { value: 'madre', label: 'Madre' },
    { value: 'padre', label: 'Padre' },
    { value: 'solo', label: 'Solo' },
    { value: 'fratello_sorella', label: 'Fratello/Sorella' },
    { value: 'nonni', label: 'Nonni' },
    { value: 'trasporto', label: 'Trasporto' },
    { value: 'altro', label: 'Altro' }
  ],
  ritiratoDa: [
    { value: 'madre', label: 'Madre' },
    { value: 'padre', label: 'Padre' },
    { value: 'solo', label: 'Solo' },
    { value: 'fratello_sorella', label: 'Fratello/Sorella' },
    { value: 'nonni', label: 'Nonni' },
    { value: 'trasporto', label: 'Trasporto' },
    { value: 'altro', label: 'Altro' }
  ],
  comportamentiProblematici: [
    { value: 'positivo', label: '✅ Positivo' },
    { value: 'aggressivo_se', label: 'Aggressivo verso sé' },
    { value: 'aggressivo_pari', label: 'Aggressivo verso pari' },
    { value: 'aggressivo_adulti', label: 'Aggressivo verso adulti' },
    { value: 'oppositivo_pari', label: 'Oppositivo verso pari' },
    { value: 'oppositivo_adulti', label: 'Oppositivo verso adulti' }
  ],
  svolgimentoCompiti: [
    { value: 'autonomo', label: '✅ Autonomo' },
    { value: 'richiede_aiuto', label: 'Richiede aiuto' },
    { value: 'svolge_con_piacere', label: 'Svolge con piacere' },
    { value: 'indifferente', label: 'Indifferente' },
    { value: 'si_rifiuta', label: 'Si rifiuta' },
    { value: 'ha_materiale', label: 'Ha materiale' },
    { value: 'no_materiale', label: 'No materiale' },
    { value: 'no_compiti', label: 'No compiti' }
  ],
  attivitaGruppo: [
    { value: 'partecipa_attivamente', label: 'Partecipa attivamente' },
    { value: 'collabora', label: 'Collabora' },
    { value: 'cerca_di_prevalere', label: 'Cerca di prevalere' },
    { value: 'passivo', label: 'Passivo' },
    { value: 'escluso', label: 'Escluso' },
    { value: 'ricercato', label: 'Ricercato' }
  ],
  tempoLibero: [
    { value: 'propone_attivita', label: 'Propone attività' },
    { value: 'accetta_proposte_compagni', label: 'Accetta proposte compagni' },
    { value: 'accetta_proposte_educatori', label: 'Accetta proposte educatori' },
    { value: 'passivo', label: 'Passivo' },
    { value: 'altro', label: 'Altro' }
  ]
}

const initialFormData = {
  minore: '',
  data: new Date().toISOString().split('T')[0],
  educatore: '',
  presente: null,  // null = non selezionato, true = presente, false = assente
  haComunicato: null,  // null = non selezionato, true = sì, false = no
  motivoAssenza: '',
  orarioIngresso: '',
  accompagnatoIngresso: '',
  accompagnatoAltro: '',
  educatoreTrasportoIngresso: '',
  notificaAssenza: false,
  orarioUscita: '',
  ritiratoDa: '',
  ritiratoAltro: '',
  educatoreTrasportoUscita: '',
  comportamentiProblematici: [],
  descrizioneComportamenti: '',
  svolgimentoCompiti: [],
  difficoltaEsercizi: '',
  attivitaGruppo: [],
  attivitaLudiche: '',
  tempoLibero: [],
  sport: '',  // Ora sarà 'Sì', 'No' o ''
  descrizioneGiornata: '',
  interventiEducativi: '',
  situazioneFamiliare: '',
  annotazioniGenitore: '',
  segnalazioneUrgente: false,
  dettaglioUrgenza: ''
}

// Componente sezione collapsabile
const CollapsibleSection = ({ id, title, expanded, onToggle, children, urgente = false, completed = false, active = false }) => (
  <Card 
    id={id}
    className={`overflow-hidden transition-all duration-300 ${
      urgente 
        ? 'border-red-300 dark:border-red-700' 
        : completed 
          ? 'border-green-400 dark:border-green-600 bg-green-50/50 dark:bg-green-900/10' 
          : active
            ? 'border-primary-400 dark:border-primary-600 ring-2 ring-primary-200 dark:ring-primary-800'
            : 'border-gray-200 dark:border-gray-700'
    }`}
  >
    <button
      type="button"
      onClick={onToggle}
      className={`w-full flex items-center justify-between p-4 text-left font-semibold ${
        urgente 
          ? 'text-red-700 dark:text-red-400' 
          : completed 
            ? 'text-green-700 dark:text-green-400' 
            : active
              ? 'text-primary-700 dark:text-primary-400'
              : 'text-gray-900 dark:text-white'
      }`}
    >
      <span className="flex items-center">
        {urgente && <AlertTriangle className="h-5 w-5 mr-2 text-red-500" />}
        {completed && !urgente && <Check className="h-5 w-5 mr-2 text-green-500" />}
        {title}
      </span>
      {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
    </button>
    {expanded && <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4">{children}</div>}
  </Card>
)

export default function NewEntryPage() {
  const { id, minoreId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, tenant } = useAuth()
  const { showSuccess, showError } = useNotification()
  const { vibrateSuccess, vibrateError } = useVibrate()
  
  const isEditing = !!id

  // Data dal query string (?data=2025-01-15), altrimenti oggi
  const dataFromUrl = searchParams.get('data')
  const defaultData = dataFromUrl || new Date().toISOString().split('T')[0]
  
  // Chiave per draft (diversa per edit vs new)
  const draftKey = isEditing ? `entry_edit_${id}` : `entry_new_${minoreId || 'new'}`
  
  // Hook per bozze locali (con gestione intelligente del tempo)
  const { 
    draft, 
    setDraft, 
    clearDraft, 
    hasDraft, 
    draftStatus, 
    formatDraftAge,
    resetToInitial 
  } = useDraft(draftKey, null)
  
  // Stato per gestione UI bozze
  const [showDraftBanner, setShowDraftBanner] = useState(false)
  const [showDraftModal, setShowDraftModal] = useState(false)
  const [bannerCountdown, setBannerCountdown] = useState(7)
  const [draftLoaded, setDraftLoaded] = useState(false)
  
  // Stato form - inizializzazione basata su draftStatus
  const [formData, setFormData] = useState(() => {
    if (isEditing) {
      return { ...initialFormData, data: defaultData, minore: minoreId || '' }
    }
    
    // Gestione intelligente della bozza
    if (draft && draftStatus === 'auto_load') {
      // < 15 min: carica automaticamente, mostrerà banner
      // Se arriva una data da URL, ha la precedenza sulla bozza
      return { ...initialFormData, ...draft, minore: minoreId || draft.minore || '', data: dataFromUrl || draft.data || defaultData }
    }
    
    if (draft && draftStatus === 'ask_user') {
      // 15 min - 1 ora: form vuoto, mostrerà modal
      return { ...initialFormData, data: defaultData, minore: minoreId || '' }
    }
    
    // Nessuna bozza o scaduta: form vuoto
    return { ...initialFormData, data: defaultData, minore: minoreId || '' }
  })
  
  // Effetto per mostrare banner/modal all'avvio
  useEffect(() => {
    if (isEditing || draftLoaded) return
    
    if (draftStatus === 'auto_load' && draft) {
      // Mostra banner informativo
      setShowDraftBanner(true)
      setDraftLoaded(true)
    } else if (draftStatus === 'ask_user' && draft) {
      // Mostra modal per chiedere
      setShowDraftModal(true)
      setDraftLoaded(true)
    }
  }, [draftStatus, draft, isEditing, draftLoaded])
  
  // Countdown banner
  useEffect(() => {
    if (!showDraftBanner) return
    
    const interval = setInterval(() => {
      setBannerCountdown(prev => {
        if (prev <= 1) {
          setShowDraftBanner(false)
          return 7
        }
        return prev - 1
      })
    }, 1000)
    
    return () => clearInterval(interval)
  }, [showDraftBanner])
  
  // Polling per controllare duplicati (ogni 7 sec)
  const checkDuplicates = useCallback(async () => {
    if (!formData.minore || !formData.data || isEditing) return null
    
    try {
      const res = await entriesApi.list({
        minore: formData.minore,
        dataInizio: formData.data,
        dataFine: formData.data
      })
      const entries = res.data?.data?.entries || res.data?.data || res.data || []
      const entriesArray = Array.isArray(entries) ? entries : []
      return entriesArray.length > 0 ? entriesArray[0] : null
    } catch {
      return null
    }
  }, [formData.minore, formData.data, isEditing])

  const { data: existingEntry } = usePolling(checkDuplicates, 7000, !isEditing && !!formData.minore)
  
  const [minori, setMinori] = useState([])
  const [users, setUsers] = useState([]) // AGGIUNTO: Lista utenti per dropdown trasporto
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [isViewMode, setIsViewMode] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})
  const [shakeFields, setShakeFields] = useState([])
  const [expandedSections, setExpandedSections] = useState({
    ingresso: false,
    uscita: false,
    comportamento: false,
    compiti: false,
    attivita: false,
    note: false,
    urgenze: false
  })
  
  // Ref per scroll automatico
  const formEndRef = useRef(null)
  
  // Stato per barra flottante
  const [showFloatingSave, setShowFloatingSave] = useState(false)
  
  // Stato per toast bozza salvata
  const [showDraftToast, setShowDraftToast] = useState(false)
  
  // Entry da eliminare (per modal)
  const [entryToDelete, setEntryToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)
  
  // Stato calendario chiusure
  const [calendarioSettings, setCalendarioSettings] = useState(null)
  const [forzaCompilazione, setForzaCompilazione] = useState(false)
  
  // Hook per avviso modifiche non salvate
  const { hasChanges, setHasChanges, confirmNavigation } = useUnsavedChanges(
    !isViewMode && !saved,
    'Hai modifiche non salvate. Vuoi davvero uscire?'
  )

  // Gestori draft modal
  const handleContinueDraft = () => {
    setFormData({ ...initialFormData, ...draft, minore: minoreId || draft?.minore || '' })
    setShowDraftModal(false)
  }
  
  const handleClearForm = () => {
    clearDraft()
    setFormData({ ...initialFormData, minore: minoreId || '' })
    setShowDraftModal(false)
    setShowDraftBanner(false)
  }
  
  const handleDismissBanner = () => {
    setShowDraftBanner(false)
  }

  // Tracking modifiche
  useEffect(() => {
    if (!loading && !isEditing && !saved) {
      const hasData = Object.keys(formData).some(key => {
        if (['minore', 'data', 'educatore'].includes(key)) return false
        const val = formData[key]
        if (Array.isArray(val)) return val.length > 0
        if (typeof val === 'boolean') return val !== false
        if (val === null) return false
        return val !== ''
      })
      setHasChanges(hasData)
    }
  }, [formData, loading, isEditing, saved, setHasChanges])

  // Salvataggio bozza automatico (debounced)
  useEffect(() => {
    if (isEditing || loading || saved || isViewMode) return
    
    const timer = setTimeout(() => {
      const hasData = Object.keys(formData).some(key => {
        if (['data', 'educatore'].includes(key)) return false
        const val = formData[key]
        if (Array.isArray(val)) return val.length > 0
        if (typeof val === 'boolean') return val !== false
        if (val === null) return false
        return val !== ''
      })
      
      if (hasData) {
        setDraft(formData)
        // Mostra toast brevemente
        setShowDraftToast(true)
        setTimeout(() => setShowDraftToast(false), 2000)
      }
    }, 3000) // Salva dopo 3s di inattività
    
    return () => clearTimeout(timer)
  }, [formData, isEditing, loading, saved, isViewMode, setDraft])

  // Caricamento iniziale
  useEffect(() => {
    loadData()
  }, [id, user])

  // Calcolo progresso compilazione
  const calculateProgress = useCallback(() => {
    let filled = 0
    let total = 0
    
    // Campi base
    if (formData.presente !== null) filled++
    total++
    
    // Se presente, conta sezioni compilate
    if (formData.presente === true) {
      // Ingresso
      if (formData.orarioIngresso || formData.accompagnatoIngresso) filled++
      total++
      
      // Uscita
      if (formData.orarioUscita || formData.ritiratoDa) filled++
      total++
    }
    
    // Comportamento
    if (formData.comportamentiProblematici?.length > 0) filled++
    total++
    
    // Compiti
    if (formData.svolgimentoCompiti?.length > 0) filled++
    total++
    
    // Attività
    if (formData.attivitaGruppo?.length > 0 || formData.tempoLibero?.length > 0) filled++
    total++
    
    // Note
    if (formData.descrizioneGiornata) filled++
    total++
    
    // Sezioni custom
    const customSections = tenant?.settings?.customSections || []
    customSections.forEach(section => {
      total++
      
      // Controlla se almeno un'opzione è selezionata
      const optionsKey = `custom_${section.id}_options`
      const hasOptions = (formData[optionsKey] || []).length > 0
      
      // Controlla se almeno una nota è compilata
      const hasNotes = section.notes?.some(note => {
        const noteKey = `custom_${section.id}_note_${note.id}`
        return !!formData[noteKey]
      })
      
      // Controlla anche le linkedNotes
      const hasLinkedNotes = section.options?.some(opt => {
        const linkedNoteKey = `custom_${section.id}_linkedNote_${opt.id}`
        return !!formData[linkedNoteKey]
      })
      
      if (hasOptions || hasNotes || hasLinkedNotes) filled++
    })
    
    return Math.round((filled / total) * 100)
  }, [formData, tenant])

  // Scroll automatico alla prossima sezione
  const scrollToNextSection = useCallback((currentSection) => {
    // Costruisci array dinamico includendo sezioni custom
    const customSections = tenant?.settings?.customSections || []
    const customSectionIds = customSections.map(s => s.id)
    const sectionOrder = ['ingresso', 'uscita', 'comportamento', 'compiti', 'attivita', 'note', 'urgenze', ...customSectionIds]
    
    const currentIndex = sectionOrder.indexOf(currentSection)
    
    // Trova la prossima sezione
    for (let i = currentIndex + 1; i < sectionOrder.length; i++) {
      const nextSection = sectionOrder[i]
      // Le sezioni custom hanno id diverso
      const isCustom = customSectionIds.includes(nextSection)
      const sectionElement = document.getElementById(isCustom ? `section-custom-${nextSection}` : `section-${nextSection}`)
      if (sectionElement) {
        setTimeout(() => {
          sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 300)
        return nextSection
      }
    }
    return null
  }, [tenant])

  // Toggle sezione con scroll automatico
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => {
      const isCurrentlyExpanded = prev[section]
      
      // Se stiamo aprendo una sezione, chiudi tutte le altre
      if (!isCurrentlyExpanded) {
        const newState = {}
        Object.keys(prev).forEach(key => {
          newState[key] = false
        })
        newState[section] = true
        return newState
      }
      
      // Se stiamo chiudendo, scrolla alla prossima sezione
      scrollToNextSection(section)
      return { ...prev, [section]: false }
    })
  }, [scrollToNextSection])

  // Mostra barra flottante quando si arriva in fondo al form
  useEffect(() => {
    if (!formEndRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Mostra la barra quando il marker di fine form è visibile
        setShowFloatingSave(entry.isIntersecting)
      },
      { threshold: 0, rootMargin: '100px' } // 100px prima di arrivare effettivamente in fondo
    )

    observer.observe(formEndRef.current)

    return () => observer.disconnect()
  }, [loading]) // ri-osserva dopo che loading è completato

  const loadData = async () => {
    try {
      setLoading(true)
      
      // Carica lista minori
      const minoriRes = await minoriApi.list({ attivo: true })
      const minoriList = minoriRes.data?.data?.minori || minoriRes.data?.minori || []
      setMinori(minoriList)
      
      // AGGIUNTO: Carica lista utenti (educatori/coordinatori) per dropdown trasporto
      try {
        const usersRes = await usersApi.list()
        const usersList = usersRes.data?.data?.users || usersRes.data?.users || []
        setUsers(usersList.filter(u => u.attivo !== false))
      } catch (usersError) {
        console.log('Lista utenti non disponibile:', usersError.message)
      }
      
      // Carica settings per calendario chiusure
      try {
        const settingsRes = await settingsApi.get()
        const settings = settingsRes.data?.data || settingsRes.data
        setCalendarioSettings(settings?.calendario || null)
      } catch (settingsError) {
        console.log('Settings calendario non disponibili:', settingsError.message)
      }

      if (isEditing) {
        // Carica entry esistente
        const entryRes = await entriesApi.get(id)
        const entry = entryRes.data?.data?.entry || entryRes.data?.entry || entryRes.data
        
        if (entry) {
          setIsViewMode(true)
          
          // Converti presente da stringa a boolean
          let presenteBool = null
          if (entry.presente === 'Sì' || entry.presente === 'Presente' || entry.presente === true) {
            presenteBool = true
          } else if (entry.presente === 'No' || entry.presente === 'Assente' || entry.presente === false) {
            presenteBool = false
          }
          
          // Estrai customFields e convertili nel formato flat del formData
          const customFieldsFlat = {}
          if (entry.customFields) {
            // customFields può essere un Map o un oggetto
            const cfObj = entry.customFields instanceof Map 
              ? Object.fromEntries(entry.customFields) 
              : (typeof entry.customFields === 'object' ? entry.customFields : {})
            
            Object.keys(cfObj).forEach(key => {
              customFieldsFlat[key] = cfObj[key]
            })
          }
          
          setFormData({
            ...initialFormData,
            ...entry,
            ...customFieldsFlat,
            minore: entry.minore?._id || entry.minore || '',
            data: entry.data ? new Date(entry.data).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            presente: presenteBool,
            haComunicato: typeof entry.haComunicato === 'boolean' ? entry.haComunicato : (entry.notificaAssenza ? true : null),
            motivoAssenza: entry.motivoAssenza || '',
            comportamentiProblematici: Array.isArray(entry.comportamentiProblematici) ? entry.comportamentiProblematici : [],
            svolgimentoCompiti: Array.isArray(entry.svolgimentoCompiti) ? entry.svolgimentoCompiti : [],
            attivitaGruppo: Array.isArray(entry.attivitaGruppo) ? entry.attivitaGruppo : [],
            tempoLibero: Array.isArray(entry.tempoLibero) ? entry.tempoLibero : []
          })
        }
      } else if (user) {
        // Imposta educatore per nuova entry
        setFormData(prev => ({
          ...prev,
          educatore: `${user.nome || ''} ${user.cognome || ''}`.trim() || user.email
        }))

        // Pre-fill da Checkin + PendingActivity (solo se non c'è bozza)
        if (minoreId && !hasDraft) {
          try {
            const dataOggi = formData.data || new Date().toISOString().split('T')[0]
            const [checkinRes, pendingRes] = await Promise.all([
              entriesApi.getCheckin(minoreId, { data: dataOggi }).catch(() => ({ data: { data: null } })),
              entriesApi.getPendingActivities(minoreId, { data: dataOggi }).catch(() => ({ data: { data: [] } }))
            ])

            const checkin = checkinRes.data?.data
            const pending = pendingRes.data?.data || []

            if (checkin || pending.length > 0) {
              setFormData(prev => {
                const updates = { ...prev }

                // Pre-fill da checkin
                if (checkin) {
                  if (checkin.presente === 'Presente') {
                    updates.presente = true
                    if (checkin.orarioIngresso) updates.orarioIngresso = checkin.orarioIngresso
                  } else if (checkin.presente === 'Assente' || checkin.presente === 'Assente giustificato') {
                    updates.presente = false
                    if (checkin.motivoAssenza) updates.motivoAssenza = checkin.motivoAssenza
                    if (checkin.presente === 'Assente giustificato') updates.haComunicato = true
                  }
                }

                // Pre-fill da pending activities
                pending.forEach(pa => {
                  if (pa.tipoAttivita === 'attivitaGruppo' && pa.valori?.length) {
                    updates.attivitaGruppo = [...new Set([...(updates.attivitaGruppo || []), ...pa.valori])]
                    if (pa.descrizione) updates.descrizioneAttivita = pa.descrizione
                  }
                  if (pa.tipoAttivita === 'tempoLibero' && pa.valori?.length) {
                    updates.tempoLibero = [...new Set([...(updates.tempoLibero || []), ...pa.valori])]
                    if (pa.descrizione) updates.attivitaLudiche = pa.descrizione
                  }
                })

                return updates
              })
            }
          } catch (prefillError) {
            console.log('Pre-fill non disponibile:', prefillError.message)
          }
        }
      }
    } catch (error) {
      console.error('Error loading data:', error)
      showError('Errore nel caricamento dei dati')
    } finally {
      setLoading(false)
    }
  }

  // Helper: verifica se un campo è abilitato nelle settings
  const isFieldEnabled = (fieldId) => {
    if (!tenant?.settings?.enabledFields) return true
    return tenant.settings.enabledFields[fieldId] !== false
  }

  // Helper: ottiene le opzioni per un campo multi-select
  // NOTA: Il backend (routes/auth.js) manda tenant.settings.fieldOptions con le opzioni personalizzate
  // Le chiavi sono: comportamentiProblematici, svolgimentoCompiti, attivitaGruppo, tempoLibero
  const getFieldOptions = (fieldId) => {
    // Mappa fieldId del form -> posizione in tenant.settings.fieldOptions
    const getCustomOptions = () => {
      switch (fieldId) {
        case 'accompagnatoIngresso':
          return tenant?.settings?.fieldOptions?.accompagnatoDaOptions
        case 'ritiratoDa':
          return tenant?.settings?.fieldOptions?.ritiratoDaOptions
        case 'comportamentiProblematici':
          return tenant?.settings?.fieldOptions?.comportamentiProblematici
        case 'svolgimentoCompiti':
          return tenant?.settings?.fieldOptions?.svolgimentoCompiti
        case 'attivitaGruppo':
          return tenant?.settings?.fieldOptions?.attivitaGruppo
        case 'tempoLibero':
          return tenant?.settings?.fieldOptions?.tempoLibero
        default:
          return tenant?.settings?.fieldOptions?.[fieldId]
      }
    }
    
    const customOptions = getCustomOptions()
    if (customOptions && Array.isArray(customOptions) && customOptions.length > 0) {
      return customOptions.map(opt => ({
        value: typeof opt === 'string' ? opt.toLowerCase().replace(/\s+/g, '_') : opt.value,
        label: typeof opt === 'string' ? opt : opt.label
      }))
    }
    return FALLBACK_OPTIONS[fieldId] || []
  }

  const updateField = (field, value) => {
    if (isViewMode) return
    setFormData(prev => ({ ...prev, [field]: value }))
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }))
    }
  }

  // Verifica se una sezione è stata compilata
  const isSectionCompleted = (sectionId) => {
    switch (sectionId) {
      case 'ingresso':
        return !!(formData.orarioIngresso || formData.accompagnatoIngresso)
      case 'uscita':
        return !!(formData.orarioUscita || formData.ritiratoDa)
      case 'comportamento':
        return !!(formData.comportamentiProblematici?.length > 0 || formData.descrizioneComportamenti)
      case 'compiti':
        return !!(formData.svolgimentoCompiti?.length > 0 || formData.difficoltaEsercizi)
      case 'attivita':
        return !!(formData.attivitaGruppo?.length > 0 || formData.tempoLibero?.length > 0 || formData.attivitaLudiche || formData.sport)
      case 'note':
        return !!(formData.descrizioneGiornata || formData.interventiEducativi || formData.situazioneFamiliare || formData.annotazioniGenitore)
      default:
        // Sezioni personalizzate: controlla se ci sono opzioni selezionate o note compilate
        const optionsKey = `custom_${sectionId}_options`
        if (formData[optionsKey]?.length > 0) return true
        
        // Controlla se qualche campo note o linkedNote ha valore
        const customPrefix = `custom_${sectionId}_`
        return Object.keys(formData).some(key => 
          key.startsWith(customPrefix) && 
          key !== optionsKey && 
          formData[key] && 
          formData[key].length > 0
        )
    }
  }

  const validateForm = () => {
    const errors = {}
    if (!formData.minore) errors.minore = 'Seleziona un minore'
    if (formData.segnalazioneUrgente && !formData.dettaglioUrgenza) {
      errors.dettaglioUrgenza = 'Descrivi l\'urgenza'
    }
    setValidationErrors(errors)
    
    // Attiva shake animation sui campi con errori
    const errorFields = Object.keys(errors)
    if (errorFields.length > 0) {
      setShakeFields(errorFields)
      setTimeout(() => setShakeFields([]), 600) // Rimuovi shake dopo animazione
    }
    
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (isViewMode) {
      setIsViewMode(false)
      return
    }
    
    if (!validateForm()) {
      vibrateError()
      showError('Compila tutti i campi obbligatori')
      return
    }

    setSaving(true)
    try {
      // Separa i campi custom dal resto
      const customFields = {}
      const standardFields = {}
      
      Object.keys(formData).forEach(key => {
        if (key.startsWith('custom_')) {
          customFields[key] = formData[key]
        } else {
          standardFields[key] = formData[key]
        }
      })
      
      // Converti presente da boolean a stringa per il backend
      let presenteValue = formData.presente
      if (formData.presente === true) {
        presenteValue = 'Sì'
      } else if (formData.presente === false) {
        presenteValue = 'No'
      }
      
      const dataToSend = {
        ...standardFields,
        presente: presenteValue,
        customFields: customFields
      }

      if (isEditing) {
        await entriesApi.update(id, dataToSend)
        showSuccess('Registrazione aggiornata')
      } else {
        await entriesApi.create(dataToSend)
        showSuccess('Registrazione salvata')
      }
      
      vibrateSuccess()
      setSaved(true)        // Prima setta saved=true
      setHasChanges(false)
      
      if (!isEditing) {
        clearDraft()        // Poi cancella la bozza (useEffect non la riscriverà perché saved=true)
      }
      
      setTimeout(() => {
        navigate('/entries')
      }, 1000)
      
    } catch (error) {
      vibrateError()
      showError(error.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handleBack = () => {
    if (hasChanges && !isViewMode) {
      confirmNavigation(() => navigate('/entries'))
    } else {
      navigate('/entries')
    }
  }

  // Elimina entry (sposta nel cestino)
  const handleDelete = async (entryId, deleteReason) => {
    try {
      setDeleting(true)
      await entriesApi.delete(entryId, deleteReason)
      showSuccess('Registrazione spostata nel cestino')
      setEntryToDelete(null)
      navigate('/entries')
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nell\'eliminazione')
    } finally {
      setDeleting(false)
    }
  }

  // Helper per visualizzare valori in view mode
  const getMultiSelectLabels = (options, values) => {
    if (!values || values.length === 0) return '-'
    return values.map(v => {
      const opt = options.find(o => o.value === v)
      return opt?.label || v
    }).join(', ')
  }

  // === HELPER PER SEZIONI CUSTOM ===
  
  // Gestisce toggle checkbox per opzione custom
  const toggleCustomOption = (sectionId, optionId) => {
    if (isViewMode) return
    const fieldKey = `custom_${sectionId}_options`
    const currentValues = formData[fieldKey] || []
    const newValues = currentValues.includes(optionId)
      ? currentValues.filter(v => v !== optionId)
      : [...currentValues, optionId]
    updateField(fieldKey, newValues)
  }
  
  // Verifica se un'opzione custom è selezionata
  const isCustomOptionSelected = (sectionId, optionId) => {
    const fieldKey = `custom_${sectionId}_options`
    return (formData[fieldKey] || []).includes(optionId)
  }
  
  // Ottiene il valore della linkedNote di un'opzione
  const getLinkedNoteValue = (sectionId, optionId) => {
    const fieldKey = `custom_${sectionId}_linkedNote_${optionId}`
    return formData[fieldKey] || ''
  }
  
  // Aggiorna il valore della linkedNote
  const updateLinkedNote = (sectionId, optionId, value) => {
    if (isViewMode) return
    const fieldKey = `custom_${sectionId}_linkedNote_${optionId}`
    updateField(fieldKey, value)
  }
  
  // Ottiene il valore di un campo nota custom
  const getCustomNoteValue = (sectionId, noteId) => {
    const fieldKey = `custom_${sectionId}_note_${noteId}`
    return formData[fieldKey] || ''
  }
  
  // Aggiorna un campo nota custom
  const updateCustomNote = (sectionId, noteId, value) => {
    if (isViewMode) return
    const fieldKey = `custom_${sectionId}_note_${noteId}`
    updateField(fieldKey, value)
  }

  // Ottieni sezioni custom dal tenant
  const customSections = tenant?.settings?.customSections || []
  
  // Controlla stato giorno per calendario chiusure
  const statoGiorno = calendarioSettings ? getStatoGiorno(formData.data, calendarioSettings) : { aperto: true }
  const giornoChiuso = !statoGiorno.aperto && !forzaCompilazione && !isEditing
  const { isCoordinatore } = useAuth()

  // === HELPER PER LOGICHE CONDIZIONALI ===
  
  // Verifica se "Trasporto" è selezionato (per accompagnatoIngresso o ritiratoDa)
  const isTrasportoSelected = (fieldValue) => {
    if (!fieldValue) return false
    const val = fieldValue.toLowerCase()
    return val === 'trasporto' || val.includes('trasporto')
  }
  
  // Verifica se "Altro" è selezionato (per accompagnatoIngresso o ritiratoDa)
  const isAltroSelected = (fieldValue) => {
    if (!fieldValue) return false
    const val = fieldValue.toLowerCase()
    return val === 'altro' || val.includes('altro')
  }
  
  // Verifica se il comportamento è "Positivo" (per nascondere descrizioneComportamenti)
  const isComportamentoPositivo = () => {
    if (!formData.comportamentiProblematici || formData.comportamentiProblematici.length === 0) return false
    // Se l'unico valore selezionato è "positivo" o "✅ Positivo"
    const vals = formData.comportamentiProblematici
    return vals.length === 1 && (vals[0] === 'positivo' || vals[0].toLowerCase().includes('positivo'))
  }
  
  // Verifica se mostrare textarea tempo libero (se "Propone attività" o "Altro")
  const shouldShowTempoLiberoTextarea = () => {
    if (!formData.tempoLibero || formData.tempoLibero.length === 0) return false
    return formData.tempoLibero.some(v => {
      const val = v.toLowerCase()
      return val === 'propone_attivita' || val.includes('propone') || val === 'altro' || val.includes('altro')
    })
  }

  if (loading) return <PageLoader />
  
  // Se giorno chiuso, mostra blocco (tranne per edit o forzatura)
  if (giornoChiuso) {
    return (
      <div className="p-4 lg:p-6">
        <div className="flex items-center mb-6">
          <button type="button" onClick={() => navigate('/entries')} className="mr-3 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Nuova Registrazione</h1>
            <p className="text-sm text-gray-500">
              {new Date(formData.data).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
        </div>
        <GiornoChiusoBlocco 
          statoGiorno={statoGiorno}
          isCoordinatore={isCoordinatore?.()}
          onForzaCompilazione={() => setForzaCompilazione(true)}
        />

        {/* Selettore data alternativa (per tutti, educatori inclusi) */}
        <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-5 w-5 text-primary-600" />
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              Compila per un altro giorno
            </h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            Seleziona un giorno aperto per procedere con la compilazione.
          </p>
          <input
            type="date"
            value={formData.data}
            max={new Date().toISOString().split('T')[0]}
            onChange={(e) => updateField('data', e.target.value)}
            className="input w-full sm:w-auto"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 lg:p-6 pb-48 lg:pb-28">
      {/* CSS Animations */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
      
      {/* Animazione salvataggio */}
      <SaveAnimation show={saved} />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button type="button" onClick={handleBack} className="mr-3 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {isEditing 
                ? (isViewMode ? 'Dettaglio Registrazione' : 'Modifica Registrazione')
                : 'Nuova Registrazione'
              }
            </h1>
            <p className="text-sm text-gray-500">
              {new Date(formData.data).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
        </div>
        
        {isViewMode ? (
          <div className="flex items-center space-x-2">
            {/* Pulsante Elimina (solo coordinatore) */}
            {isCoordinatore?.() && (
              <button
                type="button"
                onClick={() => setEntryToDelete({ _id: id, ...formData, minore: { nomeCompleto: minori.find(m => m._id === formData.minore)?.nomeCompleto } })}
                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                title="Elimina registrazione"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsViewMode(false)}
              className="btn-primary flex items-center"
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Modifica
            </button>
          </div>
        ) : (
          <button 
            type="button" 
            onClick={handleSubmit} 
            disabled={saving || saved} 
            className="btn-primary flex items-center"
          >
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Salvataggio...</>
            ) : saved ? (
              <><Check className="h-4 w-4 mr-2" />Salvato!</>
            ) : (
              <><Save className="h-4 w-4 mr-2" />Salva</>
            )}
          </button>
        )}
      </div>

      {/* Warning duplicato */}
      {existingEntry && !isEditing && (
        <DuplicateWarning 
          duplicate={{
            minoreName: minori.find(m => m._id === formData.minore)?.nomeCompleto || 'Il minore',
            educatoreName: existingEntry.educatore,
            timestamp: existingEntry.createdAt || existingEntry.data
          }}
        />
      )}
      
      {/* Warning chiusura parziale */}
      <GiornoChiusuraParzialeWarning statoGiorno={statoGiorno} />
      
      {/* Info apertura straordinaria */}
      <AperturaStroardinariaInfo statoGiorno={statoGiorno} />

      {/* Banner Bozza Recuperata (auto-load < 15 min) */}
      {showDraftBanner && !isEditing && !saved && (
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg animate-fade-in">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                Bozza recuperata ({formatDraftAge()})
              </span>
            </div>
            <span className="text-xs text-blue-500 dark:text-blue-400">
              Chiude in {bannerCountdown}s
            </span>
          </div>
          <div className="flex gap-2">
            <button 
              type="button" 
              onClick={handleClearForm}
              className="flex-1 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              🗑️ Svuota form
            </button>
            <button 
              type="button" 
              onClick={handleDismissBanner}
              className="flex-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              ✓ Continua con la bozza
            </button>
          </div>
        </div>
      )}

      {/* Modal Bozza Trovata (ask_user 15 min - 1 ora) */}
      {showDraftModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 animate-fade-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Bozza trovata
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Hai una registrazione non completata di {formatDraftAge()}
              </p>
              {draft?.minore && (
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                  📅 {draft.data ? new Date(draft.data).toLocaleDateString('it-IT') : 'Data non specificata'}
                </p>
              )}
            </div>
            
            <div className="flex gap-3">
              <button 
                type="button" 
                onClick={handleClearForm}
                className="flex-1 px-4 py-3 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                Scarta
              </button>
              <button 
                type="button" 
                onClick={handleContinueDraft}
                className="flex-1 px-4 py-3 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                Continua bozza ✓
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Card Dati Base */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Informazioni Base</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Data */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data</label>
              {isViewMode ? (
                <p className="text-gray-900 dark:text-white py-2">{new Date(formData.data).toLocaleDateString('it-IT')}</p>
              ) : (
                <input type="date" value={formData.data} onChange={(e) => updateField('data', e.target.value)} className="input" />
              )}
            </div>
            
            {/* Minore */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Minore *</label>
              {isViewMode ? (
                <p className="text-gray-900 dark:text-white py-2">
                  {minori.find(m => m._id === formData.minore)?.nomeCompleto || formData.minoreNome || '-'}
                </p>
              ) : (
                <>
                  <select 
                    value={formData.minore} 
                    onChange={(e) => updateField('minore', e.target.value)} 
                    className={`input ${validationErrors.minore ? 'border-red-500' : ''} ${shakeFields.includes('minore') ? 'animate-shake' : ''}`}
                  >
                    <option value="">Seleziona minore...</option>
                    {minori.map(m => (
                      <option key={m._id} value={m._id}>{m.nomeCompleto}</option>
                    ))}
                  </select>
                  {validationErrors.minore && (
                    <p className="text-xs text-red-500 mt-1">{validationErrors.minore}</p>
                  )}
                </>
              )}
            </div>

            {/* Educatore */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Educatore</label>
              <p className="text-gray-900 dark:text-white py-2">{formData.educatore || '-'}</p>
            </div>

            {/* Presente - Pulsanti SI/NO */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Presente</label>
              {isViewMode ? (
                <p className="text-gray-900 dark:text-white py-2">
                  {formData.presente === true ? 'Sì' : formData.presente === false ? 'No' : '-'}
                </p>
              ) : (
                <div className="flex space-x-3">
                  {/* Pulsante SI */}
                  <button
                    type="button"
                    onClick={() => updateField('presente', true)}
                    className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all duration-200 border-2 ${
                      formData.presente === true
                        ? 'bg-green-500 border-green-500 text-white shadow-md'
                        : 'bg-white dark:bg-gray-800 border-green-500 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                    }`}
                  >
                    {formData.presente === true && <Check className="inline h-4 w-4 mr-1" />}
                    SI
                  </button>
                  {/* Pulsante NO */}
                  <button
                    type="button"
                    onClick={() => updateField('presente', false)}
                    className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all duration-200 border-2 ${
                      formData.presente === false
                        ? 'bg-red-500 border-red-500 text-white shadow-md'
                        : 'bg-white dark:bg-gray-800 border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                    }`}
                  >
                    {formData.presente === false && <Check className="inline h-4 w-4 mr-1" />}
                    NO
                  </button>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Sezione Assenza - Solo se NON PRESENTE */}
        {formData.presente === false && (
          <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
            <h2 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-4">📞 Comunicazione Assenza</h2>
            
            {/* Ha comunicato? - Pulsanti SI/NO */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ha comunicato l'assenza?</label>
              {isViewMode ? (
                <p className="text-gray-900 dark:text-white py-2">
                  {formData.haComunicato === true ? 'Sì' : formData.haComunicato === false ? 'No' : '-'}
                </p>
              ) : (
                <div className="flex space-x-3">
                  {/* Pulsante SI */}
                  <button
                    type="button"
                    onClick={() => updateField('haComunicato', true)}
                    className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all duration-200 border-2 ${
                      formData.haComunicato === true
                        ? 'bg-green-500 border-green-500 text-white shadow-md'
                        : 'bg-white dark:bg-gray-800 border-green-500 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                    }`}
                  >
                    {formData.haComunicato === true && <Check className="inline h-4 w-4 mr-1" />}
                    SI
                  </button>
                  {/* Pulsante NO */}
                  <button
                    type="button"
                    onClick={() => updateField('haComunicato', false)}
                    className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all duration-200 border-2 ${
                      formData.haComunicato === false
                        ? 'bg-red-500 border-red-500 text-white shadow-md'
                        : 'bg-white dark:bg-gray-800 border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                    }`}
                  >
                    {formData.haComunicato === false && <Check className="inline h-4 w-4 mr-1" />}
                    NO
                  </button>
                </div>
              )}
            </div>

            {/* Campo motivo - Solo se ha comunicato */}
            {formData.haComunicato === true && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Motivo assenza</label>
                {isViewMode ? (
                  <p className="text-gray-900 dark:text-white py-2 whitespace-pre-wrap">{formData.motivoAssenza || '-'}</p>
                ) : (
                  <textarea 
                    value={formData.motivoAssenza || ''} 
                    onChange={(e) => updateField('motivoAssenza', e.target.value)} 
                    className="input" 
                    rows={2}
                    placeholder="Inserisci il motivo comunicato..."
                  />
                )}
              </div>
            )}
          </Card>
        )}

        {/* Sezione Ingresso - Solo se PRESENTE */}
        {formData.presente === true && isFieldEnabled('orarioIngresso') && (
          <CollapsibleSection 
            id="section-ingresso"
            title="🚪 Ingresso" 
            expanded={expandedSections.ingresso} 
            onToggle={() => toggleSection('ingresso')}
            completed={isSectionCompleted('ingresso')}
            active={expandedSections.ingresso}
          >
            <div className="space-y-4">
              {isFieldEnabled('orarioIngresso') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Orario Ingresso</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2">{formData.orarioIngresso || '-'}</p>
                  ) : (
                    <input type="time" value={formData.orarioIngresso} onChange={(e) => updateField('orarioIngresso', e.target.value)} className="input" />
                  )}
                </div>
              )}
              
              {isFieldEnabled('accompagnatoDa') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Accompagnato da</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2">{formData.accompagnatoIngresso || '-'}</p>
                  ) : (
                    <select value={formData.accompagnatoIngresso} onChange={(e) => updateField('accompagnatoIngresso', e.target.value)} className="input">
                      <option value="">Seleziona...</option>
                      {getFieldOptions('accompagnatoIngresso').map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
              
              {/* AGGIUNTO: Dropdown educatori se Trasporto */}
              {isTrasportoSelected(formData.accompagnatoIngresso) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Educatore trasporto</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2">{formData.educatoreTrasportoIngresso || '-'}</p>
                  ) : (
                    <select value={formData.educatoreTrasportoIngresso} onChange={(e) => updateField('educatoreTrasportoIngresso', e.target.value)} className="input">
                      <option value="">Seleziona educatore...</option>
                      {users.map(u => (
                        <option key={u._id} value={`${u.nome || ''} ${u.cognome || ''}`.trim() || u.email}>
                          {`${u.nome || ''} ${u.cognome || ''}`.trim() || u.email} ({u.role === 'coordinatore' ? 'Coordinatore' : 'Educatore'})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
              
              {/* AGGIUNTO: Textarea "Chi?" se Altro */}
              {isAltroSelected(formData.accompagnatoIngresso) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chi?</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2">{formData.accompagnatoAltro || '-'}</p>
                  ) : (
                    <input 
                      type="text" 
                      value={formData.accompagnatoAltro} 
                      onChange={(e) => updateField('accompagnatoAltro', e.target.value)} 
                      className="input" 
                      placeholder="Specifica chi ha accompagnato..."
                    />
                  )}
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Sezione Uscita - Solo se PRESENTE */}
        {formData.presente === true && isFieldEnabled('orarioUscita') && (
          <CollapsibleSection 
            id="section-uscita"
            title="🚶 Uscita" 
            expanded={expandedSections.uscita} 
            onToggle={() => toggleSection('uscita')}
            completed={isSectionCompleted('uscita')}
            active={expandedSections.uscita}
          >
            <div className="space-y-4">
              {isFieldEnabled('orarioUscita') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Orario Uscita</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2">{formData.orarioUscita || '-'}</p>
                  ) : (
                    <input type="time" value={formData.orarioUscita} onChange={(e) => updateField('orarioUscita', e.target.value)} className="input" />
                  )}
                </div>
              )}
              
              {isFieldEnabled('ritiratoDa') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ritirato da</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2">{formData.ritiratoDa || '-'}</p>
                  ) : (
                    <select value={formData.ritiratoDa} onChange={(e) => updateField('ritiratoDa', e.target.value)} className="input">
                      <option value="">Seleziona...</option>
                      {getFieldOptions('ritiratoDa').map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
              
              {/* AGGIUNTO: Dropdown educatori se Trasporto */}
              {isTrasportoSelected(formData.ritiratoDa) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Educatore trasporto</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2">{formData.educatoreTrasportoUscita || '-'}</p>
                  ) : (
                    <select value={formData.educatoreTrasportoUscita} onChange={(e) => updateField('educatoreTrasportoUscita', e.target.value)} className="input">
                      <option value="">Seleziona educatore...</option>
                      {users.map(u => (
                        <option key={u._id} value={`${u.nome || ''} ${u.cognome || ''}`.trim() || u.email}>
                          {`${u.nome || ''} ${u.cognome || ''}`.trim() || u.email} ({u.role === 'coordinatore' ? 'Coordinatore' : 'Educatore'})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
              
              {/* AGGIUNTO: Textarea "Chi?" se Altro */}
              {isAltroSelected(formData.ritiratoDa) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chi?</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2">{formData.ritiratoAltro || '-'}</p>
                  ) : (
                    <input 
                      type="text" 
                      value={formData.ritiratoAltro} 
                      onChange={(e) => updateField('ritiratoAltro', e.target.value)} 
                      className="input" 
                      placeholder="Specifica chi ha ritirato..."
                    />
                  )}
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Sezione Comportamento */}
        {isFieldEnabled('comportamentiProblematici') && (
          <CollapsibleSection 
            id="section-comportamento"
            title="😊 Comportamento" 
            expanded={expandedSections.comportamento} 
            onToggle={() => toggleSection('comportamento')}
            completed={isSectionCompleted('comportamento')}
            active={expandedSections.comportamento}
          >
            <div className="space-y-4">
              {isFieldEnabled('comportamentiProblematici') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Comportamento</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2">
                      {getMultiSelectLabels(getFieldOptions('comportamentiProblematici'), formData.comportamentiProblematici)}
                    </p>
                  ) : (
                    <MultiSelect
                      options={getFieldOptions('comportamentiProblematici')}
                      values={formData.comportamentiProblematici}
                      onChange={(v) => updateField('comportamentiProblematici', v)}
                    />
                  )}
                </div>
              )}
              
              {/* MODIFICATO: descrizioneComportamenti visibile SOLO se NON positivo */}
              {isFieldEnabled('descrizioneComportamenti') && !isComportamentoPositivo() && formData.comportamentiProblematici?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descrizione comportamenti</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2 whitespace-pre-wrap">{formData.descrizioneComportamenti || '-'}</p>
                  ) : (
                    <textarea 
                      value={formData.descrizioneComportamenti} 
                      onChange={(e) => updateField('descrizioneComportamenti', e.target.value)} 
                      className="input" 
                      rows={3}
                      placeholder="Descrivi i comportamenti osservati..."
                    />
                  )}
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Sezione Compiti */}
        {isFieldEnabled('svolgimentoCompiti') && (
          <CollapsibleSection 
            id="section-compiti"
            title="📚 Compiti/Studio" 
            expanded={expandedSections.compiti} 
            onToggle={() => toggleSection('compiti')}
            completed={isSectionCompleted('compiti')}
            active={expandedSections.compiti}
          >
            <div className="space-y-4">
              {isFieldEnabled('svolgimentoCompiti') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Svolgimento compiti</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2">
                      {getMultiSelectLabels(getFieldOptions('svolgimentoCompiti'), formData.svolgimentoCompiti)}
                    </p>
                  ) : (
                    <MultiSelect
                      options={getFieldOptions('svolgimentoCompiti')}
                      values={formData.svolgimentoCompiti}
                      onChange={(v) => updateField('svolgimentoCompiti', v)}
                    />
                  )}
                </div>
              )}
              
              {isFieldEnabled('difficoltaEsercizi') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Difficoltà riscontrate</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2 whitespace-pre-wrap">{formData.difficoltaEsercizi || '-'}</p>
                  ) : (
                    <textarea 
                      value={formData.difficoltaEsercizi} 
                      onChange={(e) => updateField('difficoltaEsercizi', e.target.value)} 
                      className="input" 
                      rows={2}
                      placeholder="Eventuali difficoltà negli esercizi..."
                    />
                  )}
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Sezione Attività */}
        {(isFieldEnabled('attivitaGruppo') || isFieldEnabled('tempoLibero')) && (
          <CollapsibleSection 
            id="section-attivita"
            title="🎯 Attività" 
            expanded={expandedSections.attivita} 
            onToggle={() => toggleSection('attivita')}
            completed={isSectionCompleted('attivita')}
            active={expandedSections.attivita}
          >
            <div className="space-y-4">
              {isFieldEnabled('attivitaGruppo') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Attività di gruppo</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2">
                      {getMultiSelectLabels(getFieldOptions('attivitaGruppo'), formData.attivitaGruppo)}
                    </p>
                  ) : (
                    <MultiSelect
                      options={getFieldOptions('attivitaGruppo')}
                      values={formData.attivitaGruppo}
                      onChange={(v) => updateField('attivitaGruppo', v)}
                    />
                  )}
                </div>
              )}
              
              {isFieldEnabled('tempoLibero') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tempo libero</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2">
                      {getMultiSelectLabels(getFieldOptions('tempoLibero'), formData.tempoLibero)}
                    </p>
                  ) : (
                    <MultiSelect
                      options={getFieldOptions('tempoLibero')}
                      values={formData.tempoLibero}
                      onChange={(v) => updateField('tempoLibero', v)}
                    />
                  )}
                </div>
              )}
              
              {/* AGGIUNTO: Textarea visibile se "Propone attività" o "Altro" */}
              {shouldShowTempoLiberoTextarea() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descrizione attività</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2 whitespace-pre-wrap">{formData.attivitaLudiche || '-'}</p>
                  ) : (
                    <textarea 
                      value={formData.attivitaLudiche} 
                      onChange={(e) => updateField('attivitaLudiche', e.target.value)} 
                      className="input" 
                      rows={2}
                      placeholder="Descrivi le attività proposte o altro..."
                    />
                  )}
                </div>
              )}
              
              {/* MODIFICATO: Sport con pulsanti Sì/No */}
              {isFieldEnabled('sport') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sport</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2">{formData.sport || '-'}</p>
                  ) : (
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => updateField('sport', 'Sì')}
                        className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all duration-200 border-2 ${
                          formData.sport === 'Sì'
                            ? 'bg-green-500 border-green-500 text-white shadow-md'
                            : 'bg-white dark:bg-gray-800 border-green-500 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                        }`}
                      >
                        {formData.sport === 'Sì' && <Check className="inline h-4 w-4 mr-1" />}
                        Sì
                      </button>
                      <button
                        type="button"
                        onClick={() => updateField('sport', 'No')}
                        className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all duration-200 border-2 ${
                          formData.sport === 'No'
                            ? 'bg-red-500 border-red-500 text-white shadow-md'
                            : 'bg-white dark:bg-gray-800 border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                        }`}
                      >
                        {formData.sport === 'No' && <Check className="inline h-4 w-4 mr-1" />}
                        No
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Sezione Note */}
        {isFieldEnabled('descrizioneGiornata') && (
          <CollapsibleSection 
            id="section-note"
            title="📝 Note" 
            expanded={expandedSections.note} 
            onToggle={() => toggleSection('note')}
            completed={isSectionCompleted('note')}
            active={expandedSections.note}
          >
            <div className="space-y-4">
              {isFieldEnabled('descrizioneGiornata') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descrizione giornata</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2 whitespace-pre-wrap">{formData.descrizioneGiornata || '-'}</p>
                  ) : (
                    <textarea 
                      value={formData.descrizioneGiornata} 
                      onChange={(e) => updateField('descrizioneGiornata', e.target.value)} 
                      className="input" 
                      rows={4}
                      placeholder="Descrivi come è andata la giornata..."
                    />
                  )}
                </div>
              )}
              
              {isFieldEnabled('interventiEducativi') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Interventi educativi</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2 whitespace-pre-wrap">{formData.interventiEducativi || '-'}</p>
                  ) : (
                    <textarea 
                      value={formData.interventiEducativi} 
                      onChange={(e) => updateField('interventiEducativi', e.target.value)} 
                      className="input" 
                      rows={3}
                    />
                  )}
                </div>
              )}
              
              {isFieldEnabled('situazioneFamiliare') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Situazione familiare</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2 whitespace-pre-wrap">{formData.situazioneFamiliare || '-'}</p>
                  ) : (
                    <textarea 
                      value={formData.situazioneFamiliare} 
                      onChange={(e) => updateField('situazioneFamiliare', e.target.value)} 
                      className="input" 
                      rows={2}
                    />
                  )}
                </div>
              )}
              
              {isFieldEnabled('annotazioniGenitore') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Annotazioni genitore</label>
                  {isViewMode ? (
                    <p className="text-gray-900 dark:text-white py-2 whitespace-pre-wrap">{formData.annotazioniGenitore || '-'}</p>
                  ) : (
                    <textarea 
                      value={formData.annotazioniGenitore} 
                      onChange={(e) => updateField('annotazioniGenitore', e.target.value)} 
                      className="input" 
                      rows={2}
                    />
                  )}
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Sezione Urgenze - Bottone singolo */}
        <Card className={`overflow-hidden transition-all duration-300 ${formData.segnalazioneUrgente ? 'border-red-400 dark:border-red-600 bg-red-50 dark:bg-red-900/20' : ''}`}>
          {isViewMode ? (
            // View mode
            formData.segnalazioneUrgente ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="flex items-center text-red-700 dark:text-red-400 font-semibold">
                    <AlertTriangle className="h-5 w-5 mr-2" />
                    ⚠️ URGENZA ATTIVA
                  </span>
                </div>
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-red-700 dark:text-red-300 whitespace-pre-wrap">{formData.dettaglioUrgenza || '-'}</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-sm">Nessuna segnalazione urgente</p>
            )
          ) : (
            // Edit mode
            <>
              {!formData.segnalazioneUrgente ? (
                // Bottone non attivo
                <button
                  type="button"
                  onClick={() => updateField('segnalazioneUrgente', true)}
                  className="w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 border-2 border-red-400 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center"
                >
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  ⚠️ SEGNALA URGENZA
                </button>
              ) : (
                // Bottone attivo + campo testo
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="flex items-center text-red-700 dark:text-red-400 font-semibold">
                      <AlertTriangle className="h-5 w-5 mr-2" />
                      ⚠️ URGENZA ATTIVA
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        updateField('segnalazioneUrgente', false)
                        updateField('dettaglioUrgenza', '')
                      }}
                      className="text-gray-500 hover:text-red-600 dark:hover:text-red-400 p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30"
                      title="Rimuovi segnalazione"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-700 dark:text-red-400 mb-1">Descrivi la segnalazione *</label>
                    <textarea 
                      value={formData.dettaglioUrgenza} 
                      onChange={(e) => updateField('dettaglioUrgenza', e.target.value)} 
                      className={`input border-red-300 dark:border-red-700 focus:border-red-500 focus:ring-red-500 ${validationErrors.dettaglioUrgenza ? 'border-red-500' : ''} ${shakeFields.includes('dettaglioUrgenza') ? 'animate-shake' : ''}`}
                      rows={3} 
                      placeholder="Descrivi la situazione urgente..." 
                      autoFocus
                    />
                    {validationErrors.dettaglioUrgenza && (
                      <p className="text-xs text-red-500 mt-1">{validationErrors.dettaglioUrgenza}</p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </Card>

        {/* === SEZIONI PERSONALIZZATE (Enterprise) === */}
        {customSections.map((section) => (
          <CollapsibleSection 
            key={section.id}
            id={`section-custom-${section.id}`}
            title={`✨ ${section.name}`} 
            expanded={expandedSections[section.id] || false} 
            onToggle={() => toggleSection(section.id)}
            completed={isSectionCompleted(section.id)}
            active={expandedSections[section.id] || false}
          >
            <div className="space-y-4">
              {/* OPZIONI (Checkbox multi-select) */}
              {section.options?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Seleziona opzioni
                  </label>
                  {isViewMode ? (
                    <div className="space-y-2">
                      {section.options.map(opt => {
                        const isSelected = isCustomOptionSelected(section.id, opt.id)
                        if (!isSelected) return null
                        return (
                          <div key={opt.id}>
                            <p className="text-gray-900 dark:text-white flex items-center">
                              <span className="w-4 h-4 bg-purple-500 rounded mr-2 flex items-center justify-center text-white text-xs">✓</span>
                              {opt.label}
                            </p>
                            {opt.linkedNote && getLinkedNoteValue(section.id, opt.id) && (
                              <p className="ml-6 mt-1 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-2 rounded">
                                <span className="font-medium">{opt.linkedNote}:</span> {getLinkedNoteValue(section.id, opt.id)}
                              </p>
                            )}
                          </div>
                        )
                      })}
                      {!section.options.some(opt => isCustomOptionSelected(section.id, opt.id)) && (
                        <p className="text-gray-500">Nessuna opzione selezionata</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {section.options.map(opt => {
                        const isSelected = isCustomOptionSelected(section.id, opt.id)
                        return (
                          <div key={opt.id} className={`p-3 rounded-lg border ${isSelected ? 'border-purple-300 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-700' : 'border-gray-200 dark:border-gray-700'}`}>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleCustomOption(section.id, opt.id)}
                                className="rounded text-purple-600 focus:ring-purple-500"
                              />
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{opt.label}</span>
                            </label>
                            
                            {/* LinkedNote - appare solo quando opzione selezionata */}
                            {isSelected && opt.linkedNote && (
                              <div className="mt-2 ml-6">
                                <label className="block text-xs text-purple-600 dark:text-purple-400 mb-1 flex items-center">
                                  <MessageSquare className="h-3 w-3 mr-1" />
                                  {opt.linkedNote}
                                </label>
                                <textarea
                                  value={getLinkedNoteValue(section.id, opt.id)}
                                  onChange={(e) => updateLinkedNote(section.id, opt.id, e.target.value)}
                                  className="input text-sm"
                                  rows={2}
                                  placeholder={`Inserisci ${opt.linkedNote.toLowerCase()}...`}
                                />
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
              
              {/* CAMPI NOTE (Testo libero) */}
              {section.notes?.length > 0 && (
                <div className="space-y-4">
                  {section.notes.map(note => (
                    <div key={note.id}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {note.label}
                      </label>
                      {isViewMode ? (
                        <p className="text-gray-900 dark:text-white py-2 whitespace-pre-wrap">
                          {getCustomNoteValue(section.id, note.id) || '-'}
                        </p>
                      ) : (
                        <textarea
                          value={getCustomNoteValue(section.id, note.id)}
                          onChange={(e) => updateCustomNote(section.id, note.id, e.target.value)}
                          className="input"
                          rows={3}
                          placeholder={`Inserisci ${note.label.toLowerCase()}...`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CollapsibleSection>
        ))}
      </form>

      {/* Marker invisibile per rilevare fine form */}
      <div ref={formEndRef} className="h-1" />

      {/* Modal Elimina Entry */}
      {entryToDelete && (
        <DeleteEntryModal
          entry={entryToDelete}
          onClose={() => setEntryToDelete(null)}
          onConfirm={handleDelete}
          loading={deleting}
        />
      )}

      {/* Barra flottante Salva - appare dopo scroll */}
      {showFloatingSave && !isViewMode && (
        <div className="fixed bottom-20 sm:bottom-8 left-4 right-4 mx-auto max-w-3xl bg-white/30 dark:bg-gray-800/30 backdrop-blur-xl border border-white/40 dark:border-gray-700/40 p-3 shadow-lg z-40 rounded-2xl">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700 dark:text-gray-300 hidden sm:block font-medium">
              {formData.minore ? minori.find(m => m._id === formData.minore)?.nomeCompleto : 'Nuova registrazione'}
            </span>
            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
              <button 
                type="button" 
                onClick={handleBack} 
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button 
                type="button" 
                onClick={handleSubmit} 
                disabled={saving || saved} 
                className="btn-primary flex items-center px-6"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Salvataggio...</>
                ) : saved ? (
                  <><Check className="h-4 w-4 mr-2" />Salvato!</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" />Salva</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar Fissa - sempre visibile in basso */}
      {!isViewMode && (
        <div className="fixed bottom-16 sm:bottom-0 left-0 right-0 z-50 pointer-events-none">
          <div className="h-1.5 bg-gray-200/80 dark:bg-gray-700/80">
            <div 
              className="h-full bg-gradient-to-r from-primary-500 to-green-500 transition-all duration-500 ease-out"
              style={{ width: `${calculateProgress()}%` }}
            />
          </div>
        </div>
      )}

      {/* Toast bozza salvata */}
      {showDraftToast && (
        <div className="fixed bottom-36 sm:bottom-24 right-4 bg-gray-800 dark:bg-gray-700 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50 animate-fade-in">
          ✓ Bozza salvata
        </div>
      )}
    </div>
  )
}
